// =============================================================================
// ui/state/useEntitlement.ts — "is this user Pro", for the shell.
// -----------------------------------------------------------------------------
// Read ONCE per session and held, matching the old client, which calls its
// loader from `bootAfterAuth()` and then only again from the billing card's
// retry button. Entitlement changes when a payment settles, not while somebody
// clicks between tabs.
//
// ── ⚠ THE ANSWER IS TRUE WHILE THE READ IS IN FLIGHT ──
//
// It starts UNKNOWN, and unknown fails open (see domain/entitlement.ts). The
// opposite default would flash a paywall at a paying customer on every page
// load, which is the same defect class as the cold-start banner flashing "you
// haven't logged a trade yet" at a full book.
//
// ── 🚨 …WHICH IS WHY `pending` EXISTS, AND IT IS NOT A LOADING NICETY ──
//
// Failing open plus an async read means a locked tab would render its REAL
// CONTENT for the frame before the answer lands — measured, in the wiring test:
// the Excursion tab actually mounted and started loading data. After launch a
// free user would see the Pro screen, briefly, on every visit. The old client
// has no such window: it awaits `loadEntitlement()` inside `bootAfterAuth()`
// before it paints anything.
//
// So a gated screen waits for the answer instead of guessing. ⚠ It waits ONLY
// when the switch is on — otherwise every Pro tab in beta would gain a "checking"
// flicker today for a gate that cannot fire, and a visible change is exactly
// what this phase is not allowed to make.
//
// ⚠ `pending` is about WHETHER TO DRAW YET; `pro` is about WHAT the answer is.
// A caller that only reads `pro` is correct but leaky, which is why the gate
// reads both.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadEntitlement } from '../../io/sync/entitlement';
import {
  ENTITLEMENT_UNKNOWN, PAID_LAUNCH, hasProAccess, type EntitlementRead,
} from '../../selectors/entitlement';
import { useSupabase } from './supabase';

export interface EntitlementState {
  /** True when every Pro surface should render in full. */
  pro: boolean;
  /** The raw read, for a billing card that needs to say WHY. */
  read: EntitlementRead;
  /** False until the first read settles. */
  settled: boolean;
  /**
   * 🚨 A gated screen must not draw yet — the answer is still coming.
   *
   * Always false while `PAID_LAUNCH` is off, so nothing waits for a gate that
   * cannot fire.
   */
  pending: boolean;
  /**
   * Read it again.
   *
   * ⚠ Exists for the billing card's Retry button and nothing else. Nothing
   * re-reads entitlement after boot, so without this a failed read stays failed
   * until the user happens to reload the page — and the card would keep saying
   * "we couldn't load your subscription" at somebody who is paying.
   */
  reload: () => void;
}

export function useEntitlement(): EntitlementState {
  const { sb } = useSupabase();
  const [read, setRead] = useState<EntitlementRead>(ENTITLEMENT_UNKNOWN);
  const [settled, setSettled] = useState(false);
  // ⚠ A nonce, not a flag — re-reading twice must re-run the effect twice.
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => { setSettled(false); setNonce((n) => n + 1); }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // ⚠ Never throws — see the io module. Nothing to catch, and a try here
      // would suggest otherwise to the next reader.
      const r = await loadEntitlement(sb);
      if (cancelled) return;
      setRead(r);
      setSettled(true);
    })();

    return () => { cancelled = true; };
    // ⚠ `nonce` is the whole point of the Retry button — see `reload`.
  }, [sb, nonce]);

  // ⚠ `Date.now()` at read time, not stored — a session left open across a
  // period boundary should not hold an answer computed hours ago.
  const pro = useMemo(
    () => hasProAccess({ paidLaunch: PAID_LAUNCH, read, now: Date.now() }),
    [read],
  );

  return { pro, read, settled, pending: PAID_LAUNCH && !settled, reload };
}
